import { NgClass, NgForOf, NgIf, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogModule,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { filter, takeUntil } from 'rxjs';
import { CreateStoreDialogFormComponent } from '../create-store-dialog-form/create-store-dialog-form.component';
import { StoreDto } from '../interfaces/store-dto.interface';
import { StoreCardComponent } from '../store-card/store-card.component';
import { ViewedStorePanelComponent } from '../viewed-store-panel/viewed-store-panel.component';

import { Unsub } from '../../../../services/unsub.class';
import { MerchantStoreItem } from '../types/merchant-store-item.type';

@Component({
  selector: 'create-store-container',
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule,
    NgTemplateOutlet,
    MatDialogModule,
    NgIf,
    NgClass,
    NgForOf,
    StoreCardComponent,
    ViewedStorePanelComponent,
  ],
  templateUrl: './create-store-container.component.html',
  styleUrl: './create-store-container.component.scss',
})
export class CreateStoreContainerComponent extends Unsub {
  @Input() stores: MerchantStoreItem[] | null = [];
  @Input() isActive: boolean = false;
  @Output() nextStep = new EventEmitter<void>();

  private readonly dialog = inject(MatDialog);
  cardOnViewing?: MerchantStoreItem | null;

  // ? Google Maps API
  @ViewChild('mapContainer') mapContainer!: ElementRef;
  private map: any = null;
  private circle: google.maps.Circle | undefined;
  private geocoder?: google.maps.Geocoder | null;

  private displayMap(): void {
    this.geocoder = new window.google.maps.Geocoder();

    if (!this.geocoder) {
      console.error('Geocoder not initialized yet.');
      return;
    }

    if (this.cardOnViewing && typeof this.cardOnViewing?.Address !== 'string') {
      const address = (this.cardOnViewing?.Address as any).description;
      this.cardOnViewing.Address = address;
    }

    this.geocoder.geocode(
      { address: this.cardOnViewing?.Address },
      (results, status) => {
        if (
          status === window.google.maps.GeocoderStatus.OK &&
          results &&
          results.length > 0
        ) {
          const location = results[0].geometry.location;
          this.initMap(location);
        } else {
          console.error(
            'Geocode was not successful for the following reason:',
            status
          );
        }
      }
    );
  }

  private initMap(center: google.maps.LatLng): void {
    if (this.map) {
      this.map.setCenter(center);
      this.map.setZoom(13);
      this.map.markers.forEach((marker: google.maps.Marker) =>
        marker.setMap(null)
      );
      this.map.markers = [];
      new window.google.maps.Marker({
        position: center,
        map: this.map,
        title: 'Location',
      });

      if (this.circle) {
        this.circle.setMap(null);
      }

      this.circle = new window.google.maps.Circle({
        map: this.map,
        center,
        radius: (this.cardOnViewing?.ServiceableAreaRadius ?? 5) * 1000,
        strokeColor: '#007CFF',
        strokeWeight: 1,
        fillColor: '#007CFF',
      });
      return;
    }

    this.map = new window.google.maps.Map(this.mapContainer.nativeElement, {
      center,
      zoom: 13,
    });
    this.map.markers = [];
    new window.google.maps.Marker({
      position: center,
      map: this.map,
      title: 'Location',
    });

    if (this.circle) {
      this.circle.setMap(null);
    }

    this.circle = new window.google.maps.Circle({
      map: this.map,
      center,
      radius: (this.cardOnViewing?.ServiceableAreaRadius ?? 5) * 1000,
      strokeColor: '#007CFF',
      strokeWeight: 1,
      fillColor: '#007CFF',
    });
  }

  private get defaultDialogConfig(): MatDialogConfig {
    return {
      width: '80%',
      maxWidth: '100vw',
      closeOnNavigation: false,
      disableClose: true,
    };
  }

  addStore(): void {
    const dialogRef = this.dialog.open(CreateStoreDialogFormComponent, {
      ...this.defaultDialogConfig,
      data: { stores: this.stores },
    });

    dialogRef
      .afterClosed()
      .pipe(
        takeUntil(this.unsubscribe$),
        filter((store) => !!store)
      )
      .subscribe((store: StoreDto) => {
        this.addNewlyAddedStore(store);
      });
  }

  addNewlyAddedStore(store: StoreDto): void {
    const stores = this.stores || [];
    this.stores = [store, ...stores];
  }

  editStore(store: MerchantStoreItem): void {
    const dialogRef = this.dialog.open(CreateStoreDialogFormComponent, {
      ...this.defaultDialogConfig,
      data: { store, stores: this.stores },
    });

    dialogRef
      .afterClosed()
      .pipe(
        takeUntil(this.unsubscribe$),
        filter((storeData) => !!storeData)
      )
      .subscribe((storeResult) => {
        this.updateEditedStore(storeResult);
        this.updateViewStoreCard(storeResult);
      });
  }

  updateEditedStore(editedStore: StoreDto): void {
    if (!this.stores || !editedStore) {
      return;
    }

    this.stores = this.stores.map((store) =>
      store.Id !== editedStore.Id ? store : editedStore
    );
  }

  private updateViewStoreCard(store: MerchantStoreItem): void {
    this.resetMaps();

    if (this.cardOnViewing) {
      this.viewStoreCard(store);
    }
  }

  private resetMaps(): void {
    this.map = null;
    this.geocoder = null;
  }

  viewStoreCard(store: MerchantStoreItem): void {
    this.cardOnViewing = store;
    this.resetMaps();
    this.displayMap();
  }

  onNext(): void {
    if (this.stores?.length) {
      this.nextStep.emit();
    }
  }

  closeCardViewing(): void {
    this.cardOnViewing = null;
  }
}
